/**
 * Rolling network log of recent fetch() calls — used to enrich bug reports
 * with the last N requests, especially 4xx/5xx responses that triggered
 * the user-visible problem.
 *
 * Monkey-patches window.fetch at install time. Lightweight: a single
 * try/catch wrapper, ~30 entries max, response bodies only sampled on
 * non-2xx. Auth headers and credential-looking request bodies are
 * redacted before storage.
 */

export interface NetworkEntry {
  ts: string;          // ISO start time
  method: string;
  url: string;         // path only (query stripped for the issue body, full URL kept under fullUrl)
  fullUrl: string;
  status: number;      // 0 if the call threw (network error / abort)
  durationMs: number;
  ok: boolean;
  responseSnippet?: string; // first 500 chars, only on non-2xx
  error?: string;      // present when the call threw
}

const MAX_ENTRIES = 30;
const RESPONSE_SNIPPET_LIMIT = 500;
const REDACTED = '[redacted]';

let buffer: NetworkEntry[] = [];
let installed = false;

function pushEntry(entry: NetworkEntry) {
  if (buffer.length >= MAX_ENTRIES) buffer.shift();
  buffer.push(entry);
}

function stripQuery(url: string): string {
  const q = url.indexOf('?');
  return q < 0 ? url : url.slice(0, q);
}

export function installNetworkRecorder() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const start = performance.now();
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET') ?? 'GET').toUpperCase();
    const fullUrl = input instanceof Request ? input.url : String(input);

    try {
      const res = await originalFetch(input as RequestInfo, init);
      const durationMs = Math.round(performance.now() - start);

      let snippet: string | undefined;
      if (!res.ok) {
        // Clone before any consumer reads the stream
        try {
          const clone = res.clone();
          const text = await clone.text();
          snippet = text.slice(0, RESPONSE_SNIPPET_LIMIT);
        } catch {
          // Some responses (opaque, no-cors) can't be read — that's fine
        }
      }

      pushEntry({
        ts: new Date().toISOString(),
        method,
        url: stripQuery(fullUrl),
        fullUrl,
        status: res.status,
        durationMs,
        ok: res.ok,
        responseSnippet: snippet,
      });

      return res;
    } catch (err) {
      const durationMs = Math.round(performance.now() - start);
      pushEntry({
        ts: new Date().toISOString(),
        method,
        url: stripQuery(fullUrl),
        fullUrl,
        status: 0,
        durationMs,
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  };
}

export function getNetworkLog(): NetworkEntry[] {
  return [...buffer];
}

export function clearNetworkLog() {
  buffer = [];
}

/** Pretty-printed text for inclusion in a GitHub issue body. */
export function networkLogAsText(entries: NetworkEntry[] = buffer): string {
  return entries
    .map((e) => {
      const time = e.ts.slice(11, 23);
      const status = e.status === 0 ? 'ERR' : String(e.status);
      const flag = e.ok ? '' : ' ⚠';
      const tail = e.responseSnippet
        ? ` body=${redactSnippet(e.responseSnippet).slice(0, 120)}`
        : e.error ? ` error=${e.error}` : '';
      return `${time}  ${e.method.padEnd(6)} ${e.url}  ${status} (${e.durationMs}ms)${flag}${tail}`;
    })
    .join('\n');
}

/** Drops tokens/keys that occasionally leak into error responses. */
function redactSnippet(s: string): string {
  return s
    .replace(/("?(?:access_token|refresh_token|apikey|api_key|password|secret|authorization)"?\s*[:=]\s*)("?[^",}\s]+)/gi, `$1${REDACTED}`)
    .replace(/Bearer\s+[A-Za-z0-9._-]+/g, `Bearer ${REDACTED}`);
}
